/**
 * CRYR Portal Theme System
 * 
 * This theme system is based on the login page design and will drive
 * the entire portal's visual identity.
 */

export const CRYRTheme = {
  // Color Palette - Extracted from login page
  colors: {
    // Primary Colors
    primary: '#4B92DB',           // CRYR link blue
    primaryDark: '#3a7bc8',       // Hover state
    primaryLight: '#6ba3e0',      // Light variant
    
    // Secondary Colors
    secondary: '#16243E',         // CRYR dark blue (button background)
    secondaryDark: '#0f1a2e',     // Active state
    secondaryLight: '#1a2a4a',    // Hover state
    
    // Text Colors
    text: {
      primary: '#141514',         // Main text color
      secondary: '#6E726E',       // Subtitle/secondary text
      disabled: '#4D4D4D',        // Placeholder text
      inverse: '#ffffff',         // White text
    },
    
    // Background Colors
    background: {
      primary: '#ffffff',         // White background
      secondary: '#f8f9fa',       // Light gray background
      dark: '#16243E',            // Dark background
    },
    
    // Border Colors
    border: {
      primary: '#C8C8C8',         // Default border
      focus: '#4B92DB',           // Focus border
      error: '#FC3E63',           // Error border
    },
    
    // Status Colors
    status: {
      success: '#28a745',         // Success green
      warning: '#ffc107',         // Warning yellow
      error: '#FC3E63',           // Error red (CRYR brand)
      info: '#17a2b8',            // Info blue
    },
    
    // Disabled Colors
    disabled: {
      background: '#6E726E',      // Disabled background
      text: '#4D4D4D',            // Disabled text
    }
  },

  // Typography - Based on login page
  typography: {
    fontFamily: {
      primary: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      mono: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace',
    },
    
    fontSize: {
      xs: '0.75rem',      // 12px
      sm: '0.875rem',     // 14px
      base: '1rem',       // 16px
      lg: '1.125rem',     // 18px
      xl: '1.25rem',      // 20px
      '2xl': '1.5rem',    // 24px
      '3xl': '1.875rem',  // 30px
      '4xl': '2.25rem',   // 36px
    },
    
    fontWeight: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
    },
    
    lineHeight: {
      tight: '1.25',
      normal: '1.5',
      relaxed: '1.75',
    }
  },

  // Spacing - Consistent spacing system
  spacing: {
    xs: '0.25rem',    // 4px
    sm: '0.5rem',     // 8px
    md: '1rem',       // 16px
    lg: '1.5rem',     // 24px
    xl: '2rem',       // 32px
    '2xl': '3rem',    // 48px
    '3xl': '4rem',    // 64px
  },

  // Border Radius - From login page
  borderRadius: {
    sm: '4px',
    md: '6px',        // Flutter button radius
    lg: '8px',        // Input field radius
    xl: '12px',
    full: '9999px',
  },

  // Shadows
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1)',
  },

  // Transitions
  transitions: {
    fast: '0.15s ease',
    normal: '0.2s ease',
    slow: '0.3s ease',
  },

  // Breakpoints - Responsive design
  breakpoints: {
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
    '2xl': '1400px',
  },

  // Z-Index Scale
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modal: 1040,
    popover: 1050,
    tooltip: 1060,
  }
};

// CSS Custom Properties (CSS Variables) for easy theming
export const generateCSSVariables = () => {
  const variables = {};
  
  // Colors
  Object.entries(CRYRTheme.colors).forEach(([category, values]) => {
    if (typeof values === 'object') {
      Object.entries(values).forEach(([key, value]) => {
        variables[`--color-${category}-${key}`] = value;
      });
    } else {
      variables[`--color-${category}`] = values;
    }
  });
  
  // Typography
  Object.entries(CRYRTheme.typography.fontSize).forEach(([key, value]) => {
    variables[`--font-size-${key}`] = value;
  });
  
  // Spacing
  Object.entries(CRYRTheme.spacing).forEach(([key, value]) => {
    variables[`--spacing-${key}`] = value;
  });
  
  // Border Radius
  Object.entries(CRYRTheme.borderRadius).forEach(([key, value]) => {
    variables[`--border-radius-${key}`] = value;
  });
  
  return variables;
};

// Helper functions for using the theme
export const getColor = (path) => {
  const keys = path.split('.');
  let value = CRYRTheme.colors;
  
  for (const key of keys) {
    value = value[key];
    if (value === undefined) return null;
  }
  
  return value;
};

export const getSpacing = (size) => CRYRTheme.spacing[size] || size;
export const getFontSize = (size) => CRYRTheme.typography.fontSize[size] || size;
export const getBorderRadius = (size) => CRYRTheme.borderRadius[size] || size;

export default CRYRTheme;
